import type { RouteObject } from 'react-router';

import { lazy } from 'react';
import { Navigate } from 'react-router';

import { CONFIG } from 'src/global-config';
import { DashboardLayout } from 'src/layouts/dashboard';

import { AuthGuard, AdminGuard, PermissionGuard } from 'src/auth/guard';

import { paths } from '../paths';
import { RoleScopeRedirect } from './role-scope-redirect';
import { LoadingScreenSuspenseOutlet } from '../components/route-outlet';

// ==========================================================================================
// 商户后台 — 业务菜单路由
// ==========================================================================================

/** 资产管理 */
const PageAssets = lazy(() => import('src/pages/dashboard/assets/index'));
/** 资产类目管理 */
const PageAssetsCategory = lazy(() => import('src/pages/dashboard/assets/category/index'));
/** 已删除资产（回收站） */
const PageAssetsDeleted = lazy(() => import('src/pages/dashboard/assets/deleted/index'));
/** 资产调拨记录 */
const PageAssetsTransfers = lazy(() => import('src/pages/dashboard/assets/transfers/index'));
/** 订单管理 */
const PageOrders = lazy(() => import('src/pages/dashboard/orders/index'));

/** 商品列表管理 */
const PageProducts = lazy(() => import('src/pages/dashboard/products/index'));
/** 创建商品 */
const PageProductsCreate = lazy(() => import('src/pages/dashboard/products/create/index'));
/** 编辑商品 */
const PageProductsEdit = lazy(() => import('src/pages/dashboard/products/edit/index'));
/** 商品类目管理 */
const PageProductsCategory = lazy(() => import('src/pages/dashboard/products/category/index'));

/** 门店管理 */
const PageStores = lazy(() => import('src/pages/dashboard/stores/index'));

// ==========================================================================================
// 商户后台 — 系统菜单路由
// ==========================================================================================

/** 组织管理（部门 + 员工） */
const PageOrganization = lazy(() => import('src/pages/dashboard/organization/index'));
/** 角色管理 */
const PageRoles = lazy(() => import('src/pages/dashboard/roles/index'));
/** 系统设置 */
const PageTenantProfile = lazy(() => import('src/pages/dashboard/tenant-profile/index'));
/** 个人中心 */
const PageProfile = lazy(() => import('src/pages/dashboard/profile/index'));

/** 即将上线 */
const PageComingSoon = lazy(() => import('src/pages/coming-soon/index'));

/** 财务概览 */
const PageFinanceOverview = lazy(() => import('src/pages/dashboard/finance/overview/index'));
/** 租金账单 */
const PageFinanceBills = lazy(() => import('src/pages/dashboard/finance/bills/index'));
/** 押金管理 */
const PageFinanceDeposits = lazy(() => import('src/pages/dashboard/finance/deposits/index'));
/** 收款流水 */
const PageFinancePayments = lazy(() => import('src/pages/dashboard/finance/payments/index'));
/** 退款记录 */
const PageFinanceRefunds = lazy(() => import('src/pages/dashboard/finance/refunds/index'));
/** 资金结算 */
const PageFinanceSettlements = lazy(() => import('src/pages/dashboard/finance/settlements/index'));
/** 分账参与方 */
const PageFinanceParticipants = lazy(
  () => import('src/pages/dashboard/finance/participants/index')
);
/** 清分规则 */
const PageFinanceClearingRules = lazy(
  () => import('src/pages/dashboard/finance/clearing-rules/index')
);

// ==========================================================================================
// 商户后台 — 会员菜单路由
// ==========================================================================================

// ==========================================================================================
// 商户后台 — 财务菜单路由
// ==========================================================================================

const dashboardLayout = () => (
  <DashboardLayout showWorkspacesPopover navScope="dashboard">
    <LoadingScreenSuspenseOutlet />
  </DashboardLayout>
);

const wrapWithGuard = (el: React.ReactNode) =>
  CONFIG.auth.skip ? (
    el
  ) : (
    <AuthGuard>
      <AdminGuard>
        <PermissionGuard scope="dashboard">{el}</PermissionGuard>
      </AdminGuard>
    </AuthGuard>
  );

// ----------------------------------------------------------------------

export const dashboardRoutes: RouteObject[] = [
  {
    path: 'dashboard',
    element: wrapWithGuard(dashboardLayout()),
    children: [
      { index: true, element: <RoleScopeRedirect redirectTo={paths.dashboard.root} /> },
      {
        path: 'business',
        children: [
          {
            index: true,
            element: <RoleScopeRedirect redirectTo={paths.dashboard.business.assets.root} />,
          },
          {
            path: 'assets',
            children: [
              {
                index: true,
                element: <RoleScopeRedirect redirectTo={paths.dashboard.business.assets.list} />,
              },
              { path: 'list', element: <PageAssets /> }, // 资产列表
              { path: 'category', element: <PageAssetsCategory /> }, // 资产类目
              { path: 'transfers', element: <PageAssetsTransfers /> }, // 调拨记录
              { path: 'deleted-records', element: <PageAssetsDeleted /> }, // 已删除资产（回收站）
            ],
          },
          {
            path: 'orders',
            children: [
              {
                index: true,
                element: (
                  <RoleScopeRedirect redirectTo={paths.dashboard.business.orders.rentToBuy} />
                ),
              },
              { path: 'rent-to-buy', element: <PageOrders /> }, // 订单列表
            ],
          },
          {
            path: 'products',
            children: [
              {
                index: true,
                element: <RoleScopeRedirect redirectTo={paths.dashboard.business.products.list} />,
              },
              { path: 'list', element: <PageProducts /> }, // 商品列表
              { path: 'category', element: <PageProductsCategory /> }, // 商品类目
              { path: 'create', element: <PageProductsCreate /> }, // 创建商品
              { path: 'edit/:id', element: <PageProductsEdit /> }, // 编辑商品
            ],
          },
          { path: 'stores', element: <PageStores /> },
        ],
      },
      {
        path: 'members',
        children: [
          { index: true, element: <RoleScopeRedirect redirectTo={paths.dashboard.members.list} /> },
          { path: 'list', element: <PageComingSoon /> },
        ],
      },
      {
        path: 'finance',
        children: [
          {
            index: true,
            element: <RoleScopeRedirect redirectTo={paths.dashboard.finance.overview} />,
          },
          { path: 'overview', element: <PageFinanceOverview /> }, //财务概览
          { path: 'bills', element: <PageFinanceBills /> }, //租金账单
          { path: 'deposits', element: <PageFinanceDeposits /> }, //押金管理
          { path: 'payments', element: <PageFinancePayments /> }, //收款流水
          { path: 'refunds', element: <PageFinanceRefunds /> }, //退款记录
          { path: 'settlements', element: <PageFinanceSettlements /> }, //资金结算
          { path: 'participants', element: <PageFinanceParticipants /> }, //分账参与方
          { path: 'clearing-rules', element: <PageFinanceClearingRules /> }, //清分规则
          {
            path: 'withdrawals',
            element: (
              <Navigate to={`${paths.dashboard.finance.settlements}?tab=withdrawals`} replace />
            ),
          },
          {
            path: 'clearing-records',
            element: <Navigate to={paths.dashboard.finance.settlements} replace />,
          },
        ],
      },
      {
        /** 组织管理（部门 + 员工 + 角色，当前租户维度，含总部租户） */
        path: 'organization',
        children: [
          {
            index: true,
            element: <RoleScopeRedirect redirectTo={paths.dashboard.organization.users} />,
          },
          { path: 'users', element: <PageOrganization /> },
          { path: 'roles', element: <PageRoles /> },
        ],
      },
      {
        /** 系统管理 */
        path: 'system',
        children: [
          {
            index: true,
            element: <RoleScopeRedirect redirectTo={paths.dashboard.system.settings} />,
          },
          { path: 'settings', element: <PageTenantProfile /> },
          { path: 'profile', element: <PageProfile /> },
        ],
      },
      { path: '*', element: <RoleScopeRedirect /> },
    ],
  },
];
